// Angular
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
// Afidium
import {
    IGetBookingsStayResponse,
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import { BookingResourceService } from "libraries/service/resources/booking/bookingResource.service";
import { INotification, NotificationType } from "libraries/interfaces/INotification";
import { BreadcrumbService } from "../../../../libraries/hotel/services/breadcrumb.service";
import {
    ITableServices,
    TableServicesService
} from "libraries/component/booking-information/components/sectors/components/shared/services/table-services/table-services.service";
import { mergeMap, tap } from 'rxjs/operators';
import { forkJoin, Observable, of } from "rxjs";
import { IGetEnumsService } from "libraries/service/booking/enums/IGetEnums";
import { ISectorServicesConfig } from "libraries/component/booking-information/components/sectors/components/shared/interfaces/ISectorServicesConfig";
import { BookingType } from "libraries/service/resources/booking/interfaces/get-bookings.enum";
import { EnumsResourceService } from "libraries/service/booking/enums/enums-resource.service";
import { ConfigurationResourceService } from "libraries/service/resources/configuration/configurationResource.service";
import { HotelResourceService } from "libraries/service/resources/hotel/hotelResource.service";
import { UrlHelperService } from "libraries/service/url-helper/url-helper.service";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { EStayPermissions } from "libraries/enums/e-stay-permissions";
import { flatMap } from "rxjs/internal/operators";
import { BookingDomainService } from "app_iaqobohotel/booking-page/containers/booking/services/booking-domain.service";
import { IPostReservationConfiguration } from "libraries/interfaces/i-post-reservation-configuration";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";

@Component({
    selector: 'booking',
    template: `
        <div>
            <notification *ngIf="isLoadingError"
                          [type]="notificationType">{{errorMessage}}</notification>
            <notification *ngIf="notificationAfterSuccess?.active">{{notificationAfterSuccess?.message}}</notification>
            <booking-information [bookingData]="booking"
                                 [tableServices]="tableServices"
                                 [serviceEnum]="serviceEnum"
                                 [isLoading]="isLoading"
                                 [showBookingActions]="false"></booking-information>
        </div>
    `,
    styles: [`
        notification {
            margin-bottom: .75rem;
        }
    `]
})
export class BookingComponent implements OnInit {

    public booking: IGetBookingsStayResponse;
    public isLoading: boolean = true;
    public tableServices: ITableServices;

    public notificationType: NotificationType = NotificationType.Error;
    public isLoadingError: boolean = false;
    public errorMessage: string;

    public serviceEnum: IGetEnumsService[];

    public notificationAfterSuccess: INotification;


    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly bookingResourceService: BookingResourceService,
        private readonly tableServicesService: TableServicesService,
        private readonly enumsResourceService: EnumsResourceService,
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly hotelResourceService: HotelResourceService,
        private readonly urlHelperService: UrlHelperService,
        private readonly permissionResourceService: PermissionResourceService,
        private readonly bookingDomainService: BookingDomainService,
        private readonly localizedPipe: LocalizedPipe,
    ) {
    }

    public ngOnInit(): void {
        const bookingKey: string = this.activatedRoute.snapshot.paramMap.get('booking');
        this.permissionResourceService.hasPermission(EStayPermissions.SHARED_HOST_SHOW)
            .pipe(
                flatMap((canShowHost: boolean): Observable<any> => {
                    const key: string = canShowHost ? bookingKey : this.urlHelperService.decodeUrlPart(bookingKey);
                    return this.hotelResourceService.getBookingById(
                        key,
                        {
                            computeCancellable: false,
                            computeIssuable: false,
                            computeVoidable: false,
                            format: 'shared'
                        }
                    );
                }),
                mergeMap((bookingResponse: TGetBookingResponse): Observable<[TGetBookingResponse, IGetEnumsService[], ISectorServicesConfig, IPostReservationConfiguration]> => {
                    return this.configurationBookingStream(bookingResponse);
                })
            )
            .subscribe({
                next: this.bookingLoadedNext.bind(this),
                error: (error: Error): void => {
                    this.isLoading = false;
                    this.isLoadingError = true;
                    this.errorMessage = error.message;
                }
            });
    }

    private configurationBookingStream(
        booking: TGetBookingResponse):
        Observable<[
            TGetBookingResponse,
            IGetEnumsService[],
            ISectorServicesConfig,
            IPostReservationConfiguration,
        ]> {
        return forkJoin([
            of(booking),
            booking.type ?
                booking.type.some((type: BookingType) => type === BookingType.air) ? this.enumsResourceService.listServices() : of([]) :
                of([]),
            this.bookingDomainService.getSectorsConfiguration(),
            this.bookingDomainService.getPostReservationConfiguration(),
        ]);
    }

    private bookingLoadedNext([bookingResponse, enums, servicesConfig, postReservationConfig]: [
        TGetBookingResponse,
        IGetEnumsService[],
        ISectorServicesConfig,
        IPostReservationConfiguration,
    ]): void {
        this.booking = bookingResponse as IGetBookingsStayResponse;
        this.serviceEnum = enums;

        this.tableServices = this.tableServicesService.getBookingTableServicesMapping(bookingResponse, servicesConfig, enums);
        if(postReservationConfig?.success?.messageArray?.length){
            this.notificationAfterSuccess = {
                active : true,
                message : this.localizedPipe.transform(postReservationConfig.success.messageArray),
            };
        }
        this.isLoading = false;
    }
}
